import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  TextField
} from '@mui/material';
import Navigation from './Navigation';
import RegisterStat from './RegisterStat';

const getToday = () => new Date().toISOString().split('T')[0];

const AnnotateDocumentPage = ({ data, update, onNext, onBack, onGoTo, currentIndex: propCurrentIndex }) => {
  const documents = data.documents;
  const template = data.template || [];

  // Internal state for navigation if propCurrentIndex is not provided
  const [internalIndex, setInternalIndex] = useState(0);

  // Use propCurrentIndex if defined, otherwise use internal state
  const currentIndex = typeof propCurrentIndex === 'number' ? propCurrentIndex : internalIndex;

  if (!documents || documents.length === 0) {
    return (
      <Box>
        <Typography variant="body2" color="error">
          No documents found. Please add documents before continuing.
        </Typography>
        <Button variant="outlined" onClick={() => onGoTo('start')} sx={{ mt: 2 }}>
          Back
        </Button>
      </Box>
    );
  }

  const injectDefaultMetadata = (doc) => {
    const updatedDoc = { ...doc };
    const metadata = { ...(updatedDoc.metadata || {}) };

    template.forEach((field) => {
      if (
        field.type === 'date' &&
        field.useToday &&
        !metadata[field.label]
      ) {
        metadata[field.label] = getToday();
      }
    });

    updatedDoc.metadata = metadata;
    return updatedDoc;
  };

  const currentDocCopy = injectDefaultMetadata(documents[currentIndex]);
  const updatedDocs = [...documents];
  updatedDocs[currentIndex] = currentDocCopy;

  // Only call update if metadata actually changed
  if (JSON.stringify(documents[currentIndex]) !== JSON.stringify(currentDocCopy)) {
    update({ documents: updatedDocs });
  }

  const handleFieldChange = (field, value) => {
    const updatedDoc = { ...documents[currentIndex] };
    const metadata = { ...(updatedDoc.metadata || {}) };
    metadata[field] = value;
    updatedDoc.metadata = metadata;

    const docsCopy = [...documents];
    docsCopy[currentIndex] = updatedDoc;
    update({ documents: docsCopy });
  };

  const goToNextDoc = () => {
    if (currentIndex < documents.length - 1) {
      if (typeof propCurrentIndex === 'number') {
        // If controlled by parent, ask parent to update index
        onGoTo('annotate', currentIndex + 1);
      } else {
        setInternalIndex(currentIndex + 1);
      }
    } else {
      onNext();
    }
  };

  const currentDoc = currentDocCopy;

  return (
    <Box>
      <RegisterStat
        endpoint="https://stats.soliman.nl/register"
        site="binder.soliman.nl"
        app="Binder"
        module="AnnotateDocument"
      />
      <Navigation
        currentKey="annotate"
        currentIndex={currentIndex}
        totalDocuments={documents.length}
        onGoTo={onGoTo}
      />

      <Box mb={3} mt={3}>
        <Typography variant="body1" gutterBottom>{currentDoc.file?.name}</Typography>

        {currentDoc.file?.type?.startsWith('image/') && (
          <img
            src={currentDoc.previewURL}
            alt="Preview"
            style={{ maxWidth: '100%', maxHeight: 400, border: '1px solid #ccc' }}
          />
        )}

        {currentDoc.file?.type === 'application/pdf' && (
          <iframe
            src={currentDoc.previewURL}
            title="PDF Preview"
            width="100%"
            height="500px"
            style={{ border: '1px solid #ccc' }}
          />
        )}
      </Box>

      <Stack spacing={2}>
        {template.map((field, idx) => {
          const fieldType =
            field.type === 'decimal' || field.type === 'integer'
              ? 'number'
              : field.type === 'date'
                ? 'date'
                : 'text';

          return (
            <TextField
              key={idx}
              label={field.label}
              type={fieldType}
              value={currentDoc.metadata?.[field.label] || ''}
              onChange={(e) => handleFieldChange(field.label, e.target.value)}
              InputLabelProps={field.type === 'date' ? { shrink: true } : undefined}
              fullWidth
            />
          );
        })}
      </Stack>

      <Stack direction="row" spacing={2} mt={4}>
        <Button
          variant="outlined"
          onClick={() => {
            if (currentIndex > 0) {
              if (typeof propCurrentIndex === 'number') {
                onGoTo('annotate', currentIndex - 1);
              } else {
                setInternalIndex(currentIndex - 1);
              }
            } else {
              onBack();
            }
          }}
          disabled={currentIndex === 0}
        >
          Previous document
        </Button>
        <Button variant="contained" onClick={goToNextDoc}>
          {currentIndex < documents.length - 1 ? 'Next Document' : 'Next step'}
        </Button>
      </Stack>
    </Box>
  );
};

export default AnnotateDocumentPage;
